import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, formatApiError } from "@/api";
import { toast } from "sonner";
import { Trash2, Check, Upload, LogOut, Plus, Star } from "lucide-react";

const TABS = ["reviews", "enquiries", "products", "brands", "gallery"];

function ImageField({ value, onChange }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload"
             className="flex-1 border border-border px-3 py-2 text-sm bg-transparent outline-none focus:border-primary" />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="upload-input" />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1 border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
        <Upload size={14} /> {busy ? "..." : "Upload"}
      </button>
      {value ? <img src={mediaUrl(value)} alt="" className="w-10 h-10 object-cover border border-border" /> : null}
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("reviews");
  const [reviews, setReviews] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [gallery, setGallery] = useState([]);

  const [np, setNp] = useState({ category: "", name_en: "", name_mr: "", desc_en: "", desc_mr: "", image: "", order: 0 });
  const [nb, setNb] = useState({ name: "", logo: "", order: 0 });
  const [ng, setNg] = useState({ image: "", caption_en: "", caption_mr: "", tag: "shop", order: 0 });

  const loadAll = () => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/auth/me").then(() => { setReady(true); loadAll(); })
      .catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };

  const approve = async (id) => { await api.put(`/admin/reviews/${id}/approve`); loadAll(); toast.success("Approved"); };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); loadAll(); };
  const delEnq = async (id) => { await api.delete(`/admin/enquiries/${id}`); loadAll(); };
  const addProduct = async () => {
    if (!np.name_en || !np.category) return toast.error("Category & English name required");
    await api.post("/admin/products", np); setNp({ category: "", name_en: "", name_mr: "", desc_en: "", desc_mr: "", image: "", order: products.length }); loadAll(); toast.success("Product added");
  };
  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); loadAll(); };
  const addBrand = async () => { if (!nb.name) return; await api.post("/admin/brands", nb); setNb({ name: "", logo: "", order: brands.length }); loadAll(); toast.success("Brand added"); };
  const delBrand = async (id) => { await api.delete(`/admin/brands/${id}`); loadAll(); };
  const addGallery = async () => { if (!ng.image) return toast.error("Image required"); await api.post("/admin/gallery", ng); setNg({ image: "", caption_en: "", caption_mr: "", tag: "shop", order: gallery.length }); loadAll(); toast.success("Added"); };
  const delGallery = async (id) => { await api.delete(`/admin/gallery/${id}`); loadAll(); };

  if (!ready) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;

  const inp = "w-full border border-border px-3 py-2 text-sm bg-transparent outline-none focus:border-primary";
  const pendingCount = reviews.filter((r) => !r.approved).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-[#1A1A1A] text-[#F5F2EC]">
        <div className="max-w-[1200px] mx-auto px-5 h-16 flex items-center justify-between">
          <span className="font-head font-extrabold text-lg tracking-tight">Admin Dashboard</span>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm border border-[#F5F2EC]/30 px-3 py-1.5 hover:bg-[#F5F2EC] hover:text-foreground transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 py-8">
        <div className="flex flex-wrap gap-2 border-b border-border mb-8">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                    className={`px-4 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t}{t === "reviews" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>

        {tab === "reviews" && (
          <div className="space-y-3" data-testid="admin-reviews">
            {reviews.map((r) => (
              <div key={r.id} className="border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{r.name}</span>
                    <span className="inline-flex text-accent">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</span>
                    {!r.approved && <span className="text-xs bg-accent/30 px-2 py-0.5 uppercase tracking-wide">Pending</span>}
                  </div>
                  <p className="text-sm mt-1 text-muted-foreground">{r.message}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {!r.approved && <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="p-2 border border-border hover:bg-secondary hover:text-secondary-foreground"><Check size={16} /></button>}
                  <button onClick={() => delReview(r.id)} className="p-2 border border-border hover:bg-destructive hover:text-destructive-foreground"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {reviews.length === 0 && <p className="text-muted-foreground">No reviews yet.</p>}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-3" data-testid="admin-enquiries">
            {enquiries.map((e) => (
              <div key={e.id} className="border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="font-bold">{e.name} · <a href={`tel:${e.phone}`} className="text-primary">{e.phone}</a></div>
                  <p className="text-sm mt-1 text-muted-foreground">{e.message || "—"}</p>
                </div>
                <button onClick={() => delEnq(e.id)} className="p-2 border border-border hover:bg-destructive hover:text-destructive-foreground shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
            {enquiries.length === 0 && <p className="text-muted-foreground">No enquiries yet.</p>}
          </div>
        )}

        {tab === "products" && (
          <div>
            <div className="border border-border p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="product-form">
              <input className={inp} placeholder="Category" value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} />
              <input className={inp} placeholder="Name (English)" value={np.name_en} onChange={(e) => setNp({ ...np, name_en: e.target.value })} />
              <input className={inp} placeholder="नाव (Marathi)" value={np.name_mr} onChange={(e) => setNp({ ...np, name_mr: e.target.value })} />
              <input className={inp} placeholder="Description (English)" value={np.desc_en} onChange={(e) => setNp({ ...np, desc_en: e.target.value })} />
              <input className={inp} placeholder="वर्णन (Marathi)" value={np.desc_mr} onChange={(e) => setNp({ ...np, desc_mr: e.target.value })} />
              <div className="sm:col-span-2"><ImageField value={np.image} onChange={(v) => setNp({ ...np, image: v })} /></div>
              <button onClick={addProduct} data-testid="add-product" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 font-semibold w-fit hover:bg-foreground transition-colors"><Plus size={16} /> Add product</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {products.map((p) => (
                <div key={p.id} className="border border-border p-3 flex gap-3">
                  {p.image ? <img src={mediaUrl(p.image)} alt="" className="w-16 h-16 object-cover border border-border shrink-0" /> : <div className="w-16 h-16 border border-border shrink-0 bg-muted" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase text-muted-foreground">{p.category}</div>
                    <div className="font-bold truncate">{p.name_en}</div>
                    <div className="text-sm text-muted-foreground truncate">{p.name_mr}</div>
                  </div>
                  <button onClick={() => delProduct(p.id)} className="p-2 border border-border h-fit hover:bg-destructive hover:text-destructive-foreground"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "brands" && (
          <div>
            <div className="border border-border p-5 mb-6 flex flex-col gap-3" data-testid="brand-form">
              <input className={inp} placeholder="Brand name" value={nb.name} onChange={(e) => setNb({ ...nb, name: e.target.value })} />
              <ImageField value={nb.logo} onChange={(v) => setNb({ ...nb, logo: v })} />
              <button onClick={addBrand} data-testid="add-brand" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 font-semibold w-fit hover:bg-foreground transition-colors"><Plus size={16} /> Add brand</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {brands.map((b) => (
                <div key={b.id} className="border border-border p-4 flex items-center justify-between gap-2">
                  <span className="font-semibold truncate">{b.name}</span>
                  <button onClick={() => delBrand(b.id)} className="p-2 border border-border hover:bg-destructive hover:text-destructive-foreground"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <div className="border border-border p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="gallery-form">
              <div className="sm:col-span-2"><ImageField value={ng.image} onChange={(v) => setNg({ ...ng, image: v })} /></div>
              <input className={inp} placeholder="Caption (English)" value={ng.caption_en} onChange={(e) => setNg({ ...ng, caption_en: e.target.value })} />
              <input className={inp} placeholder="मथळा (Marathi)" value={ng.caption_mr} onChange={(e) => setNg({ ...ng, caption_mr: e.target.value })} />
              <button onClick={addGallery} data-testid="add-gallery" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 font-semibold w-fit hover:bg-foreground transition-colors"><Plus size={16} /> Add image</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {gallery.map((g) => (
                <div key={g.id} className="border border-border relative group">
                  <img src={mediaUrl(g.image)} alt="" className="w-full aspect-square object-cover" />
                  <button onClick={() => delGallery(g.id)} className="absolute top-2 right-2 p-2 bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
